import { Slider } from 'antd';
import { Pause, Play, Volume2, VolumeX } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import styles from './CallRecordingPlayer.module.css';

interface CallRecordingPlayerProps {
  src: string;
}

export const formatPlaybackTime = (seconds: number): string => {
  const total =
    Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const CallRecordingPlayer: React.FC<CallRecordingPlayerProps> = ({ src }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);
  }, [src]);

  const syncDuration = () => {
    const value = audioRef.current?.duration ?? 0;
    setDuration(Number.isFinite(value) ? value : 0);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play()?.catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  };

  const seek = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  return (
    <div className={styles.player} data-testid='call-recording-player'>
      <audio
        ref={audioRef}
        src={src}
        preload='metadata'
        onLoadedMetadata={syncDuration}
        onDurationChange={syncDuration}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onError={() => {
          setIsPlaying(false);
          setHasError(true);
        }}
      />
      <button
        type='button'
        className={styles.iconButton}
        onClick={togglePlay}
        disabled={hasError}
        aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
      >
        {isPlaying ? (
          <Pause size={16} fill='currentColor' />
        ) : (
          <Play size={16} fill='currentColor' />
        )}
      </button>
      <span className={styles.time}>
        {hasError
          ? 'Recording unavailable'
          : `${formatPlaybackTime(currentTime)} / ${formatPlaybackTime(duration)}`}
      </span>
      <Slider
        className={styles.progress}
        min={0}
        max={duration || 1}
        step={0.1}
        value={Math.min(currentTime, duration || 1)}
        onChange={seek}
        disabled={hasError || !duration}
        tooltip={{ open: false }}
        aria-label='Seek recording'
      />
      <button
        type='button'
        className={styles.iconButton}
        onClick={toggleMute}
        disabled={hasError}
        aria-label={isMuted ? 'Unmute recording' : 'Mute recording'}
      >
        {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
};

export default CallRecordingPlayer;
